import React, { useEffect, useMemo } from 'react';
import moment from 'moment';
import * as filterKeyMap from '@/pages/intelligence/components/Filter/FilterBase/_config';
import useMarketFilterApi, {
  MarketSpecailType,
} from '@/pages/intelligence/hooks/useMarketSelector';
import { handleDetailRetentionFilterParamsToApi } from '@/pages/intelligence/pages/TopCharts/_config/topchartFilterCfg';
import { FilterTimeType } from '@intelligence/utils/filterUtils';
import RetentionMonthlyContext from './index';
import type { TopchartFilterCfgType } from '@intelligence/types/topchart';
import {
  getStatsRetentionValidUrlParams,
  getCountriesRPDValidUrlParams,
  statsRetentionValidCfg,
  countriesRetentionValidCfg,
  hasShortLinkRatio,
  getRetentionPageValidCfg,
} from '@intelligence/_config/urlPageValidCfg';
import { useShortLink } from '@intelligence/hooks/useShortLink';
import { useIntl } from 'umi';
export default function RetentionMonthlyComp(props: any) {
  const { urlFilterValue, tabType, entityType, dataSource } = props;

  const isSensorTowerMonthly = dataSource === 'sensortower';

  const { formatMessage, locale } = useIntl();
  const { filterLoading, regionOptions } = useMarketFilterApi({
    filterType: MarketSpecailType.retention,
  });

  const isCoutriesPage = tabType === 'countries';
  const currentValidCfg = isCoutriesPage ? countriesRetentionValidCfg : statsRetentionValidCfg;
  const getValidUrlParams = isCoutriesPage
    ? getCountriesRPDValidUrlParams
    : getStatsRetentionValidUrlParams;
  // 是否有短链接更新功能
  const hasUrlShortLink = isCoutriesPage
    ? hasShortLinkRatio.detailMobileCountriesRetention
    : hasShortLinkRatio.detailMobileStatsRetention;
  const {
    initShortLinkCfgNoAwait,
    shortLinkLoading,
    urlCustomParams,
    shortLinkFilterValue,
    shortLinkValidQuery,
  } = useShortLink({
    urlFilterValue,
    hasUrlShortLink,
    getPageValidCfg: (urlFilter) => {
      const { statsValidCfg, countriesValidCfg } = getRetentionPageValidCfg(
        urlFilter,
        currentValidCfg,
      );
      return isCoutriesPage ? countriesValidCfg : statsValidCfg;
    },
    getCustomValidUrlParams: getValidUrlParams,
  });

  useEffect(() => {
    // 进入页面时判断是否有tokenQuery等，有则进入
    initShortLinkCfgNoAwait();
  }, []);

  const defaultFilterValue = useMemo(
    () => ({
      [filterKeyMap.REGION]: {
        regions: [],
        markets: ['global'],
        type: 'market',
      },
      [filterKeyMap.RETENTION]: {
        value: ['D2', 'D3', 'D7'],
      },
      [filterKeyMap.RANGE_TIME]: {
        type: 'month',
        value: ['', ''],
      },
      [filterKeyMap.PLATFORM]: {
        value: ['appstore', 'googleplay'],
      },
    }),
    [],
  );

  const sensorTowerEarliestMonth = useMemo(() => moment('2025-01-01').startOf('month'), []);
  const rangeTimeSpecialParams: any = useMemo(
    () => {
      if (!isSensorTowerMonthly) {
        return {};
      }
      return {
        [filterKeyMap.RANGE_TIME]: {
          disabledDate: (current: moment.Moment) =>
            current?.isValid?.()
              ? current.isBefore(sensorTowerEarliestMonth, 'month')
              : false,
        },
      };
    },
    [isSensorTowerMonthly, sensorTowerEarliestMonth],
  );

  const filterCfg: TopchartFilterCfgType = useMemo(
    () => ({
      filterKeyList: [
        filterKeyMap.RANGE_TIME,
        filterKeyMap.REGION,
        filterKeyMap.RETENTION,
        filterKeyMap.PLATFORM,
      ],
      regionFilterParams: {},
      defaultValue: defaultFilterValue,
      resetFilterValue: {
        ...defaultFilterValue,
      },
      specialParams: {
        filterTips: {
          [filterKeyMap.REGION]: formatMessage({
            id: 'dbi_info_retention_monthly_region_filter_hover',
          }),
          [filterKeyMap.PLATFORM]: formatMessage({
            id: 'dbi_info_retention_monthly_platform_filter_hover',
          }),
        },
        regionOptions, // 国家筛选器options,
        defalutMarket: ['global'], // 没取到交集用的默认值
        disableNoChina: false,
        rangeTimeType: FilterTimeType.rangeMonth,
        isShowAlloftime: false,
        globalInfoKey: 'dbi_info_global_filter2',
        showShortcut: true,
        entityType,
        ...rangeTimeSpecialParams,
      },
      showConfirmButton: false,
      haveFilterSaveBtn: false,
      handleFilterToApiParams: (filterParams: any) => {
        const api = handleDetailRetentionFilterParamsToApi(filterParams);
        if (!isSensorTowerMonthly || !api?.platforms) return api;
        const p = api.platforms;
        if (Array.isArray(p) && p.length === 1 && p[0] === 'unified') {
          return { ...api, platforms: ['appstore', 'googleplay'] };
        }
        return api;
      },
      hasUrlShortLink,
    }),
    [
      hasUrlShortLink,
      defaultFilterValue,
      regionOptions,
      locale,
      entityType,
      isSensorTowerMonthly,
      rangeTimeSpecialParams,
      sensorTowerEarliestMonth,
    ],
  );

  return (
    <RetentionMonthlyContext
      {...props}
      isCoutriesPage={isCoutriesPage}
      filterLoading={filterLoading || shortLinkLoading}
      // 筛选器相关配置如筛选器展示顺序，默认值等
      filterCfg={filterCfg}
      // 从短链接中拿到校正后的url参数（与页面相关）
      urlCustomParams={urlCustomParams}
      // 筛选器有效的页面配置值
      shortLinkValidQuery={shortLinkValidQuery}
      // 从短链接拿到的筛选器分享值
      shortLinkFilterValue={shortLinkFilterValue}
    />
  );
}
